import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { DataManager, Visualisation, Toolbar, Chart, FilterBar } from 'wc-visualisation';
import { Icon, Popover, formatDate, FieldType, downloadCsv, arrayToCsv } from 'wc-lib';
import * as screenfull from 'screenfull';
import { NemRegions } from '../config';
import { insertMissingIntervals } from './dataUtils';
import { getAlphanumericString } from '../../../helpers/stringHelper';
// import { FPP_CONSTRAINT_FREQUENCY_MEASURE_1HR_testData } from './test-data/FPP_CONSTRAINT_FREQUENCY_MEASURE_1HR-test-data';
// import { FPP_CONSTRAINT_FREQUENCY_MEASURE_3HR_testData } from './test-data/FPP_CONSTRAINT_FREQUENCY_MEASURE_3HR-test-data';
// import { FPP_CONSTRAINT_FREQUENCY_MEASURE_24HR_testData } from './test-data/FPP_CONSTRAINT_FREQUENCY_MEASURE_24HR-test-data';
// import { FPP_FREQUENCY_DEVIATION_AND_MEASURE_1HR_testData } from './test-data/FPP_FREQUENCY_DEVIATION_AND_MEASURE_1HR-test-data';
// import { FPP_FREQUENCY_DEVIATION_AND_MEASURE_3HR_testData } from './test-data/FPP_FREQUENCY_DEVIATION_AND_MEASURE_3HR-test-data';
// import { FPP_FREQUENCY_DEVIATION_AND_MEASURE_24HR_testData } from './test-data/FPP_FREQUENCY_DEVIATION_AND_MEASURE_24HR-test-data';

require('wc-lib/icons/downloadcircle.svg');
require('wc-lib/icons/fullscreen.svg');

@Component({
  template: require('./fpp-frequency-measure.html'),
  name: 'fpp-frequency-measure',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class FppFrequencyMeasure extends Vue {
  constraintFrequencyMeasureTab = 'CONSTRAINT_FREQUENCY_MEASURE';
  regionalFrequencyDeviationTab = 'REGIONAL_FREQUENCY_DEVIATION';
  regionFrequencyMeasureTab = 'REGION_FREQUENCY_MEASURE';

  purple = '#B056BC';
  lightBlue = '#34B9B3';

  selectedTab: string = this.regionalFrequencyDeviationTab;
  tabs = [{ value: this.regionalFrequencyDeviationTab, label: 'Regional Frequency Deviation' }, { value: this.regionFrequencyMeasureTab, label: 'Region Frequency Measure' }, { value: this.constraintFrequencyMeasureTab, label: 'Constraint Frequency Measure' }]

  selectedRegionId: string = 'NSW1';
  regions: { label: string, value: string }[] = NemRegions;

  selectedConstraintId: string = '';
  constraintIds = [];

  intervals = [{ value: '1HR', label: '1 hr' }, { value: '3HR', label: '3 hrs' }, { value: '24HR', label: '24 hrs' }]
  selectedInterval: string = this.intervals[0].value;

  loadMessage: string = '';
  dataCache: Record<string, any[]> = {};
  data: any[] = [];
  chartData: any[] = [];
  chartConfig: any = null;

  tableData: any[] = [];
  tableColumns: any[] = [];

  intervalRefresh: any;

  created() {
    if (screenfull.isEnabled) {
      (screenfull as any).onchange(() => {
        this.isFullscreen = (screenfull as any).isFullscreen;
      });
    }

    this.loadChartData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, true);

    this.intervalRefresh = setInterval(() => {
      this.loadChartData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, true);
    }, 300 * 1000);
  }

  destroyed() {
    clearInterval(this.intervalRefresh)
  }
  
  selectTab(tab: string): void {
    this.selectedTab = tab;
    this.loadChartData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, false);
  }

  @Watch('selectedConstraintId')
  onSelectedConstraintIdChange(newValue: string, oldValue: string): void {
    this.selectedConstraintId = newValue;
    this.loadChartData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, false);
  }

  @Watch('selectedRegionId')
  onSelectedRegionIdChange(newValue: string, oldValue: string): void {
    this.selectedRegionId = newValue;
    this.loadChartData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, false);
  }

  @Watch('selectedInterval')
  onSelectedIntervalChange(newValue: string, oldValue: string): void {
    this.selectedInterval = newValue;
    this.loadChartData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, false);
  }

  loadChartData(tab: string, constraintId: string, interval: string, regionId: string, clearDataCache: boolean) {
    this.loadMessage = 'Loading data';
    this.fetchDataAsync(tab, interval, clearDataCache).then(
      (data) => {
        this.data = data;

        let filteredData: any[];
        switch (tab) {
          case this.constraintFrequencyMeasureTab:
            this.constraintIds = Array.from(new Set<string>(data.map(x => x.CID))).sort();
            if (this.constraintIds.indexOf(constraintId) === -1) {
              this.selectedConstraintId = this.constraintIds[0];
              constraintId = this.constraintIds[0];
            }

            filteredData = data.filter(x => x.CID === constraintId);
            this.chartData = insertMissingIntervals(filteredData, 'MDT', 4, { FM: 0 });
            this.chartConfig = this.constraintFrequencyMeasureChartConfig;
            this.tableData = filteredData;
            this.tableColumns = this.constraintFrequencyMeasureTableColumns;
            break;

          case this.regionalFrequencyDeviationTab:
            filteredData = data.filter(x => x.RID === regionId);
            this.chartData = insertMissingIntervals(filteredData, 'MDT', 4, { FD: 0, FM: 0 });
            this.chartConfig = this.regionalFrequencyDeviationChartConfig;
            this.tableData = filteredData;
            this.tableColumns = this.regionalFrequencyDeviationTableColumns;
            break;

          case this.regionFrequencyMeasureTab:
            let newIntervalProperties = {};
            for (let regionId of NemRegions.map(x => x.value)) {
              newIntervalProperties[regionId] = 0;
            }

            let tempData = this.getRegionFrequencyMeasureChartData(data);
            this.chartData = insertMissingIntervals(tempData, 'MDT', 4, newIntervalProperties);
            this.chartConfig = this.getRegionFrequencyMeasureChartConfig();
            this.tableData = tempData;
            this.tableColumns = this.regionFrequencyMeasureTableColumns;
            break;
        }

        this.loadMessage = '';
      },
      (error) => {
        this.loadMessage = 'No data found';
      });
  }

  async fetchDataAsync(tab: string, interval: string, clearDataCache: boolean): Promise<any[]> {
    if (clearDataCache) {
      this.dataCache = {};
    }

    let reportName = tab === this.constraintFrequencyMeasureTab ? `FPP_${tab}_${interval}` : `FPP_FREQUENCY_DEVIATION_AND_MEASURE_${interval}`;

    if (!this.dataCache[reportName]) {
      let id = await DataManager.addEndPoint('../api/report/' + reportName, undefined, 60000, reportName, undefined, 'yyyy-MM-ddTHH:mm:ss', ['MDT']);
      let data = DataManager.getDynamicView(id, reportName).data();

      // let data: any[];
      // if (tab === this.constraintFrequencyMeasureTab) {
      //   if (this.selectedInterval === '1HR') data = FPP_CONSTRAINT_FREQUENCY_MEASURE_1HR_testData;
      //   else if (this.selectedInterval === '3HR') data = FPP_CONSTRAINT_FREQUENCY_MEASURE_3HR_testData;
      //   else if (this.selectedInterval === '24HR') data = FPP_CONSTRAINT_FREQUENCY_MEASURE_24HR_testData;
      // }
      // else {
      //   if (this.selectedInterval === '1HR') data = FPP_FREQUENCY_DEVIATION_AND_MEASURE_1HR_testData;
      //   else if (this.selectedInterval === '3HR') data = FPP_FREQUENCY_DEVIATION_AND_MEASURE_3HR_testData;
      //   else if (this.selectedInterval === '24HR') data = FPP_FREQUENCY_DEVIATION_AND_MEASURE_24HR_testData;
      // }
      // data = data.map((x) => ({ ...x, MDT: new Date(Date.parse(x.MDT)) }));

      this.dataCache[reportName] = data;
    }

    return this.dataCache[reportName];
  }

  getRegionFrequencyMeasureChartData(data: any[]): any[] {
    let chartData: any[] = [];

    // group data by MDT datetime. 
    // [{MDT: "yyyymmdd", RID: "NSW1", FM: 0.1}, {MDT: "yyyymmdd", RID: "VIC1", FM: 0.2}] becomes [{MDT: "yyyymmdd", NSW1: 0.1, VIC1: 0.2}]
    for (let datum of data) {
      let chartDatum = chartData.find(x => x.MDT.getTime() === datum.MDT.getTime());
      if (chartDatum) {
        chartDatum[datum.RID] = datum.FM;
      }
      else {
        let newChartDatum = { MDT: datum.MDT };
        newChartDatum[datum.RID] = datum.FM;
        chartData.push(newChartDatum);
      }
    }

    return chartData;
  }

  //
  // chart
  //

  constraintFrequencyMeasureChartConfig = {
    type: 'serial',
    marginRight: 0,
    marginLeft: 0,
    marginTop: 10,
    marginBottom: 0,
    addClassNames: true,
    panEventsEnabled: false,
    numberFormatter: {
      precision: 5,
      decimalSeparator: '.',
      thousandsSeparator: ','
    },
    valueAxes: [
      {
        id: 'FrequencyMeasure',
        position: "left",
        labelsEnabled: true,
        gridAlpha: 0,
        axisAlpha: 0,
        color: '#8ea9a9',
        title: 'FREQUENCY MEASURE (Hz)',
        titleColor: this.purple
      },
    ],
    graphs: [
      {
        valueAxis: 'FrequencyMeasure',
        balloonText: '[[value]] Hz',
        lineColor: this.purple,
        valueField: 'FM',
        lineThickness: 1,
        balloonColor: '#000000',
        balloon: {
          color: '#ffffff',
          fillColor: '#000000',
          showBullet: false
        }
      },
    ],
    categoryAxis: {
      parseDates: true,
      minPeriod: "ss",
      color: '#8ea9a9',
      gridAlpha: 0,
      labelsEnabled: true,
      axisAlpha: 0
    },
    categoryField: "MDT",
    chartCursor: {
      zoomable: false,
      bulletsEnabled: true,
      bulletSize: 12,
      cursorColor: '#000000',
      categoryBalloonFunction: function (value) {
        return formatDate(value, "HH:mm:ss dd MMM");
      }
    }
  };

  regionalFrequencyDeviationChartConfig = {
    type: 'serial',
    marginRight: 0,
    marginLeft: 0,
    marginTop: 10,
    marginBottom: 0,
    addClassNames: true,
    panEventsEnabled: false,
    numberFormatter: {
      precision: 5,
      decimalSeparator: '.',
      thousandsSeparator: ','
    },
    valueAxes: [
      {
        id: 'Frequency',
        position: "left",
        labelsEnabled: true,
        gridAlpha: 0,
        axisAlpha: 0,
        color: '#8ea9a9',
        title: 'Frequency (Hz)',
        titleColor: '#000000'
      }
    ],
    graphs: [
      {
        valueAxis: 'Frequency',
        balloonText: 'Frequency Deviation [[value]] Hz',
        lineColor: this.purple,
        valueField: 'FD',
        lineThickness: 1,
        balloonColor: '#000000',
        balloon: {
          color: '#ffffff',
          fillColor: '#000000',
          showBullet: false
        }
      },
      {
        valueAxis: 'Frequency',
        balloonText: 'Frequency Measure [[value]] Hz',
        lineColor: this.lightBlue,
        valueField: 'FM',
        lineThickness: 1,
        balloonColor: '#000000',
        balloon: {
          color: '#ffffff',
          fillColor: '#000000',
          showBullet: false
        }
      }
    ],
    legend: {
      align: 'right',
      position: 'top',
      data: [{ title: 'Frequency Deviation', color: this.purple }, { title: 'Frequency Measure', color: this.lightBlue }],
      equalWidths: true,
    },
    categoryAxis: {
      parseDates: true,
      minPeriod: "ss",
      color: '#8ea9a9',
      gridAlpha: 0,
      labelsEnabled: true,
      axisAlpha: 0
    },
    categoryField: "MDT",
    chartCursor: {
      zoomable: false,
      bulletsEnabled: true,
      bulletSize: 12,
      cursorColor: '#000000',
      categoryBalloonFunction: function (value) {
        return formatDate(value, "HH:mm:ss dd MMM");
      }
    }
  };

  getRegionFrequencyMeasureChartConfig(): any {
    let graphs: any[] = [];
    let legendData: any[] = [];
    for (let region of NemRegions) {
      let regionColor: string = 'black';
      switch (region.value) {
        case 'NSW1': regionColor = '#a4dbe8'; break;
        case 'QLD1': regionColor = '#9b2242'; break;
        case 'VIC1': regionColor = '#7c7fab'; break;
        case 'SA1': regionColor = '#e56a54'; break;
        case 'TAS1': regionColor = '#a1d884'; break;
      }
      graphs.push({
        valueAxis: 'FrequencyMeasure',
        balloonText: `${region.label} [[value]] Hz`,
        lineColor: regionColor,
        lineThickness: 1,
        valueField: region.value,
        balloonColor: '#000000',
        balloon: {
          color: '#ffffff',
          fillColor: '#000000',
          showBullet: false
        }
      });
      legendData.push({ title: region.label, color: regionColor });
    }

    return {
      type: 'serial',
      marginRight: 0,
      marginLeft: 0,
      marginTop: 10,
      marginBottom: 0,
      addClassNames: true,
      panEventsEnabled: false,
      numberFormatter: {
        precision: 5,
        decimalSeparator: '.',
        thousandsSeparator: ','
      },
      valueAxes: [
        {
          id: 'FrequencyMeasure',
          position: "left",
          labelsEnabled: true,
          gridAlpha: 0,
          axisAlpha: 0,
          color: '#8ea9a9',
          title: 'FREQUENCY MEASURE (Hz)',
          titleColor: 'black'
        },
      ],
      graphs,
      legend: {
        align: 'right',
        position: 'top',
        data: legendData,
        equalWidths: true,
      },
      categoryAxis: {
        parseDates: true,
        minPeriod: "ss",
        color: '#8ea9a9',
        gridAlpha: 0,
        labelsEnabled: true,
        axisAlpha: 0
      },
      categoryField: "MDT",
      chartCursor: {
        zoomable: false,
        bulletsEnabled: true,
        bulletSize: 12,
        cursorColor: '#000000',
        categoryBalloonFunction: function (value) {
          return formatDate(value, "HH:mm:ss dd MMM");
        }
      }
    }
  };

  //
  // table
  //

  constraintFrequencyMeasureTableColumns = [
    {
      title: 'Market Date Time',
      field: 'MDT',
      formatter: (value) => {
        let date = value.MDT || value;
        return formatDate(date, 'dd/MM/yyyy HH:mm:ss');
      },
      sortable: true
    },
    {
      title: 'Constraint ID',
      field: 'CID',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Frequency Measure (Hz)',
      field: 'FM',
      fieldType: FieldType.Decimal,
      sortable: true
    },
  ];

  regionalFrequencyDeviationTableColumns = [
    {
      title: 'Market Date Time',
      field: 'MDT',
      formatter: (value) => {
        let date = value.MDT || value;
        return formatDate(date, 'dd/MM/yyyy HH:mm:ss');
      },
      sortable: true
    },
    {
      title: 'Region ID',
      field: 'RID',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Frequency Deviation (Hz)',
      field: 'FD',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'Frequency Measure (Hz)',
      field: 'FM',
      fieldType: FieldType.Decimal,
      sortable: true
    },
  ];

  regionFrequencyMeasureTableColumns = [
    {
      title: 'Market Date Time',
      field: 'MDT',
      formatter: (value) => {
        let date = value.MDT || value;
        return formatDate(date, 'dd/MM/yyyy HH:mm:ss');
      },
      sortable: true
    },
    {
      title: 'NSW Frequency Measure (Hz)',
      field: 'NSW1',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'QLD Frequency Measure (Hz)',
      field: 'QLD1',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'VIC Frequency Measure (Hz)',
      field: 'VIC1',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'SA Frequency Measure (Hz)',
      field: 'SA1',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'TAS Frequency Measure (Hz)',
      field: 'TAS1',
      fieldType: FieldType.Decimal,
      sortable: true
    },
  ];

  //
  // download
  //

  download() {
    let filename = 'FPP_' + this.selectedTab;
    if (this.selectedTab === this.constraintFrequencyMeasureTab) {
      filename += '_' + getAlphanumericString(this.selectedConstraintId);
    }
    else if (this.selectedTab === this.regionalFrequencyDeviationTab) {
      filename += '_' + this.selectedRegionId;
    }
    filename += `_${this.selectedInterval}_${formatDate(this.tableData[0].MDT, 'yyyyMMddHHmmss')}.csv`

    let rows = this.tableData;
    let columns = this.tableColumns;
    let csv = arrayToCsv(rows, columns);
    downloadCsv(filename, csv);
  }

  //
  // full screen
  //

  isFullscreen = false;

  toggleFullscreen() {
    (screenfull as any).toggle();
    setTimeout(() => {
      if (!this.isFullscreen) {
        this.loadChartData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, false);
      }
    }, 2000); // delay gives screen.isFullscreen time to update after screen.toggle()
  }
}


// WEBPACK FOOTER //
// src/views/electricity/fpp/fpp-frequency-measure.ts